import React, { useContext } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import UserContext from "./UserContext";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_MUTED = "rgba(15, 17, 33, 0.55)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const INK_HAIRLINE_SOFT = "rgba(15, 17, 33, 0.05)";
const ACCENT = "#3854e8";
const ACCENT_SOFT = "rgba(56, 84, 232, 0.12)";
const SURFACE = "#ffffff";
const CANVAS = "#f6f6f4";

const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";
const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 }
};
const viewportConfig = { once: true, margin: "-60px" };
const springTransition = { type: "spring", stiffness: 110, damping: 22 };

const primaryButtonSx = {
  fontFamily: SANS,
  fontSize: "0.94rem",
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2.5,
  py: 1.25,
  borderRadius: "12px",
  color: "#fafaf7",
  backgroundColor: INK,
  boxShadow:
    "inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 8px 18px -10px rgba(15, 17, 33, 0.45)",
  transition:
    "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "#1a1d36",
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const ghostButtonSx = {
  fontFamily: SANS,
  fontSize: "0.94rem",
  fontWeight: 500,
  textTransform: "none",
  letterSpacing: "0.2px",
  px: 2.5,
  py: 1.25,
  borderRadius: "12px",
  color: INK,
  border: `1px solid ${INK_HAIRLINE}`,
  backgroundColor: SURFACE,
  transition: "all 0.15s ease",
  "&:hover": {
    borderColor: "rgba(15, 17, 33, 0.32)",
    backgroundColor: SURFACE,
    transform: "translateY(-1px)"
  },
  "&:active": { transform: "translateY(0) scale(0.985)" }
};

const PLANS = [
  {
    name: "Free",
    price: "0",
    cents: "00",
    description:
      "Enough for steady prep. Ten feedback loops a day, every practice mode included.",
    features: [
      { text: "10 feedback loops a day", emphasized: true },
      { text: "All four practice modes" },
      { text: "Personalized written feedback" },
      { text: "Save questions to revisit" }
    ],
    cta: { label: "Sign up free", to: "/login", primary: false }
  },
  {
    name: "Pro",
    price: "9",
    cents: "99",
    description:
      "Lift the daily ceiling. Everything else is identical to Free.",
    features: [
      { text: "Unlimited feedback loops", emphasized: true },
      { text: "All four practice modes" },
      { text: "Personalized written feedback" },
      { text: "Save questions to revisit" }
    ],
    accent: true,
    badge: "Most picked",
    cta: { label: "Get Pro", to: "/login", primary: true }
  }
];

const PlanCard = ({ plan }) => {
  const isAccent = !!plan.accent;
  const { user } = useContext(UserContext);
  // Logged-in users hitting "Get Pro" should land on the checkout, not the
  // login page. The Free plan's "Sign up free" CTA stays /login regardless;
  // logged-in users can ignore it.
  const ctaTo =
    plan.name === "Pro" && user ? "/upgrade" : plan.cta.to;
  return (
    <Box
      component={motion.div}
      {...fadeUp}
      viewport={viewportConfig}
      transition={springTransition}
      sx={{
        position: "relative",
        backgroundColor: SURFACE,
        borderRadius: "20px",
        border: `1px solid ${isAccent ? ACCENT : INK_HAIRLINE}`,
        boxShadow: isAccent
          ? "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 0 0 4px rgba(56, 84, 232, 0.08), 0 24px 60px -28px rgba(15, 17, 33, 0.22)"
          : "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 4px 10px -6px rgba(15, 17, 33, 0.06)",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        overflow: "hidden"
      }}
    >
      {plan.badge && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            px: 1,
            py: 0.3,
            borderRadius: "999px",
            backgroundColor: ACCENT_SOFT,
            color: ACCENT,
            fontFamily: MONO,
            fontSize: "0.62rem",
            fontWeight: 500,
            letterSpacing: "0.14em",
            textTransform: "uppercase"
          }}
        >
          {plan.badge}
        </Box>
      )}
      <Box sx={{ p: { xs: 3, md: 4 } }}>
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: "0.7rem",
            fontWeight: 500,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: isAccent ? ACCENT : INK_MUTED,
            mb: 2.25
          }}
        >
          {plan.name}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            mb: 1.25
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.9rem",
              color: INK_FAINT,
              lineHeight: 1
            }}
          >
            $
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "3.6rem",
              fontWeight: 500,
              letterSpacing: "-0.04em",
              color: INK,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 0.95
            }}
          >
            {plan.price}
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "1.4rem",
              fontWeight: 500,
              letterSpacing: "-0.03em",
              color: INK_FAINT,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1
            }}
          >
            .{plan.cents}
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: "0.92rem",
              color: INK_FAINT,
              ml: 0.75
            }}
          >
            / month
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: "0.98rem",
            lineHeight: 1.55,
            color: INK_SOFT,
            maxWidth: "38ch"
          }}
        >
          {plan.description}
        </Typography>
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${INK_HAIRLINE_SOFT}`,
          px: { xs: 3, md: 4 },
          py: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.4
        }}
      >
        {plan.features.map((feat, i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 1.25,
              alignItems: "start"
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: feat.emphasized
                  ? isAccent
                    ? ACCENT
                    : INK
                  : isAccent
                  ? ACCENT_SOFT
                  : "rgba(15, 17, 33, 0.05)",
                color: feat.emphasized
                  ? "#fafaf7"
                  : isAccent
                  ? ACCENT
                  : INK,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 0.25,
                flexShrink: 0
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 12 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: "0.95rem",
                lineHeight: 1.5,
                color: feat.emphasized ? INK : INK_SOFT,
                fontWeight: feat.emphasized ? 600 : 400
              }}
            >
              {feat.text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${INK_HAIRLINE_SOFT}`,
          px: { xs: 3, md: 4 },
          py: 3
        }}
      >
        <Button
          component={Link}
          to={ctaTo}
          disableRipple
          endIcon={
            plan.cta.primary ? (
              <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
            ) : undefined
          }
          sx={{
            ...(plan.cta.primary ? primaryButtonSx : ghostButtonSx),
            width: "100%"
          }}
        >
          {plan.cta.label}
        </Button>
      </Box>
    </Box>
  );
};

export default function PaymentPlans() {
  return (
    <Box
      id="pricing"
      sx={{
        position: "relative",
        backgroundColor: CANVAS,
        py: { xs: 8, md: 14 },
        px: { xs: 2, sm: 3 },
        textAlign: "left",
        overflow: "hidden"
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(15, 17, 33, 0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 17, 33, 0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.4), transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.4), transparent 78%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Container
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 1, textAlign: "left" }}
      >
        <Box
          component={motion.div}
          {...fadeUp}
          viewport={viewportConfig}
          transition={springTransition}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 7fr) minmax(0, 5fr)"
            },
            gap: { xs: 3, md: 6 },
            alignItems: "end",
            mb: { xs: 6, md: 9 }
          }}
        >
          <Box>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5
              }}
            >
              <Box
                component={motion.span}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ACCENT
                }}
              />
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: INK_MUTED
                }}
              >
                Pricing
              </Typography>
            </Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3rem" },
                lineHeight: 1.02,
                letterSpacing: "-0.03em",
                color: INK,
                m: 0,
                mb: 2,
                maxWidth: "16ch"
              }}
            >
              Two plans. Pick the one you need.
            </Typography>
            <Typography
              sx={{
                fontFamily: SANS,
                fontSize: { xs: "1rem", md: "1.08rem" },
                lineHeight: 1.55,
                color: INK_SOFT,
                m: 0,
                maxWidth: "52ch"
              }}
            >
              Free is enough to try the loop. Pro is what you reach for the week
              before an interview.
            </Typography>
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
              pb: 0.5
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: "0.66rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: INK_FAINT,
                maxWidth: "26ch",
                textAlign: "right"
              }}
            >
              Cancel anytime · No card to start free
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 3, md: 4 },
            alignItems: "stretch"
          }}
        >
          {PLANS.map(plan => (
            <PlanCard key={plan.name} plan={plan} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}
